export function unwrap(payload) {
  if (
    payload &&
    typeof payload === "object" &&
    !Array.isArray(payload) &&
    "data" in payload &&
    "success" in payload
  ) {
    return payload.data;
  }
  return payload;
}

const LIST_KEYS = [
  "items", "results", "businesses", "projects", "rows", "list", "records",
  "failures", "rejections", "templates", "countries", "data",
];

export function extractRows(data) {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== "object") return [];
  for (const k of LIST_KEYS) if (Array.isArray(data[k])) return data[k];
  const nested = Object.values(data).find((v) => Array.isArray(v));
  return nested || [];
}

export function extractTotal(data, fallback = 0) {
  if (!data || typeof data !== "object") return fallback;
  for (const k of ["total", "total_elements", "totalElements", "total_count", "totalCount", "count"]) {
    if (typeof data[k] === "number") return data[k];
  }
  return fallback;
}

export function pickId(row, keys) {
  for (const k of keys) {
    if (row && row[k] !== undefined && row[k] !== null && row[k] !== "") return row[k];
  }
  return undefined;
}

export const BUSINESS_ID_KEYS = ["wydely_business_id", "business_id", "wydelyBusinessId", "id"];
export const PROJECT_ID_KEYS = ["wydely_project_id", "project_id", "wydelyProjectId", "id"];

export function epochParams(range) {
  if (!range?.from) return {};
  const params = { from_epoch: Math.floor(range.from.getTime() / 1000) };
  if (range.to) params.to_epoch = Math.floor(range.to.getTime() / 1000);
  return params;
}

export function findCounts(obj, keys) {
  if (!obj || typeof obj !== "object") return null;
  const lower = {};
  for (const [k, v] of Object.entries(obj)) lower[k.toLowerCase()] = v;
  const hits = keys.filter((k) => typeof lower[k] === "number");
  if (hits.length >= 2) return Object.fromEntries(hits.map((k) => [k, lower[k]]));
  for (const v of Object.values(obj)) {
    if (v && typeof v === "object" && !Array.isArray(v)) {
      const found = findCounts(v, keys);
      if (found) return found;
    }
  }
  return null;
}

export function findBuckets(obj) {
  if (Array.isArray(obj)) {
    if (obj.length && typeof obj[0] === "object" && obj[0] !== null) {
      const keys = Object.keys(obj[0]);
      if (keys.some((k) => /date|day|bucket|_ts|time/i.test(k))) return obj;
    }
    return null;
  }
  if (!obj || typeof obj !== "object") return null;
  for (const v of Object.values(obj)) {
    const found = findBuckets(v);
    if (found) return found;
  }
  return null;
}

const IST_FORMATTER = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Asia/Kolkata",
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

export function formatIST(date) {
  if (date === null || date === undefined || date === "") return "—";
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return "—";
  return IST_FORMATTER.format(d);
}

export const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2})?(\.\d+)?(Z|[+-]\d{2}:?\d{2})?$/;

export function parseUTCDate(s) {
  let str = String(s).trim();
  if (!/(Z|[+-]\d{2}:?\d{2})$/.test(str)) str += "Z";
  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
}

export function formatNestedDates(value) {
  if (Array.isArray(value)) return value.map(formatNestedDates);
  if (value && typeof value === "object") {
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, formatNestedDates(v)]));
  }
  if (typeof value === "string" && ISO_DATE_RE.test(value.trim())) {
    const d = parseUTCDate(value);
    if (d) return formatIST(d);
  }
  return value;
}
